/**
 * JWT Authentication Client
 * Handles token storage, retrieval, and refresh logic for NestJS backend
 */

const API_URL = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3002/api/v1';

/**
 * Auth is unversioned — `/api/auth`, not `/api/v1/auth`.
 *
 * The URI version is for entity REST, where a resource's shape can change under clients. Signing
 * in has no such shape, and pinning it to v1 makes the version something auth has to move in
 * lockstep with — including the refresh cookie's Path, which fails SILENTLY when it drifts.
 * Derived from the configured base so it follows host and protocol without a second setting.
 */
const AUTH_URL = `${API_URL.replace(/\/v\d+\/?$/, '')}/auth`;

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';
// Record-keyed invite session tokens (ADR-0003, #137). An invite session is scoped to a single
// Work Response, so each one is stored under `invite_token:<responseId>` rather than fighting over
// the single `access_token` slot. This lets a person hold several invited records open at once
// (across tabs or within a tab) without one invite evicting another's session — the cause of the
// cross-record "Access denied" 403s. `access_token` still holds the most-recently-activated
// session (for the middleware cookie + the global auth-context user).
const INVITE_TOKEN_PREFIX = 'invite_token:';
// Routing flag for middleware.js — never a token. See setInviteSessionMarker.
const INVITE_SESSION_COOKIE = 'invite_session';
const OBJECT_ID_RE = /^[0-9a-f]{24}$/i;

export interface AuthTokens {
  accessToken: string;
  refreshToken: string;
}

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterData {
  name: string;
  email: string;
  password: string;
  organisationName?: string;
}

export interface PendingVerification {
  email: string;
  accountStatus: 'unverified';
}

export interface VerifiedSession extends AuthTokens {
  accountStatus?: string;
  requiresOrgSetup?: boolean;
}

export interface AuthUser {
  _id?: string;
  email: string;
  name?: string;
  organisationId?: string;
  organisationName?: string;
  role?: string;
  image?: string;
  isScoped?: boolean;
  /**
   * Account state from the user document. Both are absent on an invite session,
   */
  isActive?: boolean;
  accountStatus?: 'unverified' | 'invited' | 'active';
  /** Set when a business-domain signup has no organisation yet and must create one. */
  requiresOrgSetup?: boolean;
  /**
   * Principal type. Absent for a real user document; 'invite' for an
   * account-less invite session (ADR-0003); 'partner' for a partner principal.
   */
  type?: 'partner' | 'invite';
  scope?: 'vendor-response' | 'client-brief';
  contactId?: string;
  workRequestId?: string;
  workResponseId?: string;
  inviteId?: string;
}

/**
 * True when the principal is an account-less invite session (ADR-0003).
 * This drives the "lite" invitee experience that previously keyed off the
 * now-removed `user.isStub` flag.
 */
export function isInviteSession(user: AuthUser | null | undefined): boolean {
  return user?.type === 'invite';
}

/**
 * Adopt an INVITE session token as the active one.
 *
 * A signed-in user's session is no longer written here: login, register, refresh and the OAuth
 * callback set an HttpOnly cookie server-side, and nothing in the browser can read it — which is
 * the point. This previously wrote the access token to localStorage AND to a JS-readable cookie,
 * so one XSS took both copies, and the refresh token with them.
 *
 * `middleware.js` still routes on the `access_token` cookie, and still can: middleware runs on
 * the server, where HttpOnly cookies are readable. An INVITE session is the case that needed
 * care — it has no such cookie — which is what `setInviteSessionMarker` exists for.
 */
export function setTokens(tokens: { accessToken: string; refreshToken?: string }): void {
  if (typeof window !== 'undefined') {
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    if (tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
  }
}

/**
 * Store an invite session token keyed by the Work Response it is scoped to (#137), and make it
 * the active global session. Keeping a per-record entry means opening a second invite no longer
 * evicts the first — each record's API calls resolve their own token via {@link getAccessTokenForUrl}.
 */
export function setInviteToken(responseId: string, token: string): void {
  if (typeof window === 'undefined' || !responseId) return;
  localStorage.setItem(`${INVITE_TOKEN_PREFIX}${responseId}`, token);
  // The just-verified record becomes the active session for the auth-context user and any
  // non-record-scoped request.
  setTokens({ accessToken: token });
  setInviteSessionMarker();
}

/**
 * Tell `middleware.js` that an invite session exists — without giving it the token.
 *
 * The middleware routes on a cookie, and an ordinary user's is the HttpOnly one the API sets.
 * An invite session has no such cookie: its token is record-scoped, lives in localStorage, and
 * must never become the ambient session. Without some cookie the middleware sees no session at
 * all and bounces an invitee to /signin on their own record.
 *
 * So this is a flag, not a credential — it carries no token, grants nothing, and is only ever
 * read to decide "has a session, and it is invite-scoped". Authorisation stays with the API.
 */
export function setInviteSessionMarker(): void {
  if (typeof window === 'undefined') return;
  // Optional-chained: some non-browser hosts stub a window without a location.
  const secure = window.location?.protocol === 'https:' ? '; Secure' : '';
  const expires = new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${INVITE_SESSION_COOKIE}=1; path=/; expires=${expires}; SameSite=Lax${secure}`;
}

/**
 * Wipe anything the previous occupant of this browser left in client storage.
 *
 * Called on a successful sign-in. The new session is a cookie, so nothing needs to be written —
 * but a leftover token or invite marker would still be picked up, and signing in as someone else
 * must not inherit their records. `getAccessToken` already refuses stale USER tokens by claim;
 * this clears the rest, including per-record invite tokens.
 */
export function clearClientSessionRemnants(): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  for (let i = localStorage.length - 1; i >= 0; i--) {
    const key = localStorage.key(i);
    if (key && key.startsWith(INVITE_TOKEN_PREFIX)) localStorage.removeItem(key);
  }
  document.cookie = `${INVITE_SESSION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

/** Read the invite session token scoped to a given Work Response, if any (#137). */
export function getInviteToken(responseId: string): string | null {
  if (typeof window === 'undefined' || !responseId) return null;
  return localStorage.getItem(`${INVITE_TOKEN_PREFIX}${responseId}`);
}

/**
 * Decode an invite session JWT into a display identity (#137). The signature is NOT verified —
 * this is for showing the right invitee (name/email) in the UI; the backend still authorises every
 * request. Returns null for an empty/malformed token or a non-invite (user) token.
 */
export function decodeInviteToken(token: string): AuthUser | null {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    let b64 = part.replace(/-/g, '+').replace(/_/g, '/');
    b64 += '='.repeat((4 - (b64.length % 4)) % 4); // restore padding atob requires
    const json = typeof atob !== 'undefined'
      ? atob(b64)
      : Buffer.from(b64, 'base64').toString('utf-8');
    const payload = JSON.parse(json);
    if (payload?.type !== 'invite') return null;
    return {
      type: 'invite',
      email: payload.email,
      name: payload.name,
      contactId: payload.contactId,
      workRequestId: payload.workRequestId,
      workResponseId: payload.workResponseId,
      inviteId: payload.inviteId,
      organisationId: payload.organisationId,
    };
  } catch {
    return null;
  }
}

/**
 * The display identity for the invite session scoped to a given Work Response, if one is held
 * (#137) — so each tab/route shows its own invitee rather than the most-recently-opened one.
 */
export function getInviteIdentity(responseId: string): AuthUser | null {
  const token = getInviteToken(responseId);
  return token ? decodeInviteToken(token) : null;
}

/**
 * Resolve the Work Response id a request URL targets, for record-keyed invite-token selection
 * (#137). Recognises the invite-session-reachable endpoints:
 *   - /work-responses/{id} and its subroutes (/body, /send-brief, …)
 *   - /questions?parentType=workResponse&parentId={id}
 *   - /documents/entity/WorkResponse/{id}
 * Returns null for collection routes (/work-responses, /work-responses/invited) and anything else.
 */
export function resolveResponseIdFromUrl(url: string): string | null {
  if (!url) return null;
  const qIdx = url.indexOf('?');
  const path = qIdx >= 0 ? url.slice(0, qIdx) : url;
  const search = qIdx >= 0 ? url.slice(qIdx + 1) : '';

  let m = path.match(/\/work-responses\/([0-9a-f]{24})(?:\/|$)/i);
  if (m) return m[1];

  m = path.match(/\/documents\/entity\/WorkResponse\/([0-9a-f]{24})(?:\/|$)/i);
  if (m) return m[1];

  if (search) {
    const params = new URLSearchParams(search);
    if (params.get('parentType') === 'workResponse') {
      const parentId = params.get('parentId');
      if (parentId && OBJECT_ID_RE.test(parentId)) return parentId;
    }
  }
  return null;
}

/**
 * Pick the Authorization token for a request URL (#137): the invite session scoped to the URL's
 * Work Response when one exists, otherwise the global token (real user, or the active invite). A
 * record-scoped URL with no invite token (e.g. a logged-in user viewing a WRes) falls back cleanly.
 */
export function getAccessTokenForUrl(url: string): string | null {
  const responseId = resolveResponseIdFromUrl(url);
  if (responseId) {
    const inviteToken = getInviteToken(responseId);
    if (inviteToken) return inviteToken;
  }
  return getAccessToken();
}

/**
 * The token to put in an Authorization header, if there is one to put.
 *
 * Returns null for an ordinary signed-in user: their session is an HttpOnly cookie the server
 * set, which JS cannot read and does not need to — the browser attaches it to every same-origin
 * request. A null here is not "signed out"; requests must therefore be sent with
 * `credentials: 'include'` and let the cookie speak.
 *
 * What DOES come back is an Invite Session token. Those stay in localStorage because they are
 * scoped to one record and several can be live at once, which a single ambient cookie cannot
 * express (see `setInviteToken` and the API's session-cookie.ts).
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(ACCESS_TOKEN_KEY);
  if (!raw) return null;

  // ONLY an invite token is honoured from here. A user session is the HttpOnly cookie, and the
  // API reads the Authorization header FIRST — so a user token left in this slot outranks the
  // cookie and answers as whoever it belongs to. Before the cookie move, `login()` overwrote
  // this slot on every sign-in, which hid the problem; now nothing writes it, so a token from a
  // pre-move session kept authenticating as that account no matter who signed in afterwards.
  // Checking the claim rather than clearing on login also heals sessions already in that state.
  return decodeInviteToken(raw) ? raw : null;
}

/**
 * Get refresh token from localStorage or cookie
 * Tries localStorage first, then falls back to cookie
 */
export function getRefreshToken(): string | null {
  if (typeof window !== 'undefined') {
    // Try localStorage first
    const localToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (localToken) return localToken;

    // Fallback to cookie (set by server-side OAuth callback)
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === REFRESH_TOKEN_KEY) {
        // Also sync to localStorage for consistency
        localStorage.setItem(REFRESH_TOKEN_KEY, value);
        return value;
      }
    }
  }
  return null;
}

/**
 * Clear all tokens (logout)
 * Removes from both localStorage AND cookies
 */
export function clearTokens(): void {
  if (typeof window !== 'undefined') {
    // Clear localStorage
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);

    // Clear every record-keyed invite session token (#137).
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (key && key.startsWith(INVITE_TOKEN_PREFIX)) localStorage.removeItem(key);
    }

    // Clear cookie by setting expiry to past date
    document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
    document.cookie = `${INVITE_SESSION_COOKIE}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
  }
}

/**
 * Check if user is authenticated (has access token)
 */
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

/**
 * Login with email and password
 */
export async function login(credentials: LoginCredentials): Promise<AuthTokens> {
  // The route is `/auth/login`. This posted `/auth/signin`, which 404s — password sign-in
  // through the form never reached the API at all.
  const response = await fetch(`${AUTH_URL}/login`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(credentials),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Login failed');
  }

  const data = await response.json();
  const tokens: AuthTokens = {
    accessToken: data.accessToken || data.access_token,
    refreshToken: data.refreshToken || data.refresh_token,
  };

  // Not stored: /auth/login set the session as an HttpOnly cookie. Keeping a second copy in
  // localStorage would hand it straight back to any XSS and defeat the cookie entirely.
  //
  // Whatever the last occupant of this browser left behind IS cleared, though — stale invite
  // tokens and markers included. Signing in as someone else must not inherit their records.
  clearClientSessionRemnants();
  return tokens;
}

/**
 */
export async function register(data: RegisterData): Promise<PendingVerification> {
  const response = await fetch(`${AUTH_URL}/register`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ ...data, frontendBaseUrl: window.location.origin }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Registration failed');
  }

  return response.json();
}

export async function verifyEmail(token: string): Promise<VerifiedSession> {
  const response = await fetch(`${AUTH_URL}/verify-email`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ token }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'This confirmation link is no longer valid.');
  }

  clearClientSessionRemnants();
  return response.json();
}

export async function resendVerification(email: string): Promise<{ message: string }> {
  const response = await fetch(`${AUTH_URL}/resend-verification`, {
    credentials: 'include',
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ email, frontendBaseUrl: window.location.origin }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Could not send a new link.');
  }

  return response.json();
}

/**
 * Refresh access token using refresh token
 */
export async function refreshAccessToken(): Promise<string | null> {
  // May be null for a cookie-only session: the refresh cookie is HttpOnly and scoped to this
  // endpoint, so the browser attaches it without JS ever seeing it. Bailing out on a missing
  // localStorage copy would make every ordinary session unrefreshable.
  const refreshToken = getRefreshToken();

  try {
    const response = await fetch(`${AUTH_URL}/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        ...(refreshToken ? { Authorization: `Bearer ${refreshToken}` } : {}),
      },
    });

    if (!response.ok) {
      // Refresh token is invalid or expired
      clearTokens();
      return null;
    }

    const data = await response.json();
    const newAccessToken = data.accessToken || data.access_token;
    const newRefreshToken = data.refreshToken || data.refresh_token;

    // A cookie-only session gets nothing back here — /auth/refresh rotated the HttpOnly cookie
    // and the browser follows. A session holding a localStorage copy must be told, or the next
    // refresh presents the token this call just retired.
    if (newRefreshToken && typeof window !== 'undefined') {
      localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    }

    return newAccessToken;
  } catch (error) {
    console.error('Token refresh failed:', error);
    clearTokens();
    return null;
  }
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  const refreshToken = getRefreshToken();

  // Call logout endpoint to invalidate refresh token on backend
  if (refreshToken) {
    try {
      await fetch(`${AUTH_URL}/logout`, {
      credentials: 'include',
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${getAccessToken()}`,
        },
        body: JSON.stringify({ refreshToken }),
      });
    } catch (error) {
      console.error('Logout API call failed:', error);
    }
  }

  // Clear tokens from localStorage
  clearTokens();
}

/**
 * Fetch current user profile from API
 */
export async function getCurrentUser(): Promise<AuthUser | null> {
  // No early return on a missing token. An ordinary session is an HttpOnly cookie the browser
  // attaches itself, so there is nothing here to read — bailing out on that treated every
  // signed-in user as signed out, and login appeared to do nothing at all. The header is added
  // only for an Invite Session, which is the one principal that still carries a token in JS.
  const token = getAccessToken();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : undefined;

  try {
    const response = await fetch(`${AUTH_URL}/me`, {
      credentials: 'include',
      headers: { ...(authHeader ?? {}) },
    });

    if (!response.ok) {
      if (response.status === 401) {
        // Token expired, try to refresh
        const newToken = await refreshAccessToken();
        if (newToken) {
          const retryResponse = await fetch(`${AUTH_URL}/me`, {
            credentials: 'include',
            headers: { ...(token ? { Authorization: `Bearer ${newToken}` } : {}) },
          });
          if (retryResponse.ok) {
            return await retryResponse.json();
          }
        }
        // Refresh failed, clear tokens
        clearTokens();
        return null;
      }
      throw new Error('Failed to fetch user profile');
    }

    const user = await response.json();
    return user;
  } catch (error) {
    console.error('Error fetching current user:', error);
    return null;
  }
}

/**
 * OAuth login redirect helper
 */
export function loginWithGoogle(): void {
  const returnUrl = encodeURIComponent(window.location.origin);
  window.location.href = `${AUTH_URL}/google?returnUrl=${returnUrl}`;
}

export function loginWithMicrosoft(): void {
  const returnUrl = encodeURIComponent(window.location.origin);
  window.location.href = `${AUTH_URL}/microsoft?returnUrl=${returnUrl}`;
}

/**
 * Handle OAuth callback (extract tokens from URL)
 */
export function handleOAuthCallback(): AuthTokens | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const params = new URLSearchParams(window.location.search);
  const accessToken = params.get('access_token') || params.get('accessToken');
  const refreshToken = params.get('refresh_token') || params.get('refreshToken');

  if (accessToken && refreshToken) {
    const tokens: AuthTokens = { accessToken, refreshToken };
    // app/auth/callback/route.ts already wrote these as HttpOnly cookies before redirecting.

    // Clean up URL
    window.history.replaceState({}, document.title, window.location.pathname);

    return tokens;
  }

  return null;
}
